import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { RevealOnScroll } from "@/components/site/Reveal";
import { ArrowRight } from "lucide-react";

const TYPES = [
  { id: "manufacturer", label: "Manufacturer" },
  { id: "fleet", label: "Fleet / Corporate" },
  { id: "supplier", label: "International Supplier" },
  { id: "dealership", label: "Dealership Partner" },
  { id: "other", label: "Other" },
];

export default function Partner() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organisation: "", role: "",
    partner_type: "manufacturer", message: "", country: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/partner-enquiries", form);
      setDone(true);
      toast.success("Received. A member of the Maklan team will be in touch.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const c = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="partner-page" className="pt-40 pb-32">
      <div className="mx-auto max-w-[1300px] px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-6">Partner With Maklan</div>
            <RevealOnScroll
              element="h1"
              className="h-editorial text-[clamp(2.6rem,6.5vw,5.5rem)]"
              lines={["Some doors open", "by <em>invitation</em>."]}
            />
            <p className="mt-8 text-white/75 text-lg font-light leading-relaxed">
              Manufacturers, fleets, suppliers and dealership partners with the
              standards we recognise are welcome to begin a conversation.
              We reply to every considered enquiry.
            </p>
            <div className="mt-12 space-y-4 text-white/60 text-sm">
              <div><span className="text-[#C5A059]">◆</span>&nbsp;&nbsp;Manufacturers seeking a considered gateway into Africa.</div>
              <div><span className="text-[#C5A059]">◆</span>&nbsp;&nbsp;Enterprises procuring fleets at scale.</div>
              <div><span className="text-[#C5A059]">◆</span>&nbsp;&nbsp;Logistics, warranty, insurance and finance houses.</div>
              <div><span className="text-[#C5A059]">◆</span>&nbsp;&nbsp;Regional dealership partners across the continent.</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                data-testid="partner-success"
                className="border border-[#C5A059]/40 bg-[#C5A059]/5 p-10"
              >
                <div className="overline mb-3">Received</div>
                <h3 className="font-display text-3xl mb-3">Thank you.</h3>
                <p className="text-white/70">A member of the Maklan team will respond by written correspondence shortly.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="partner-form" className="space-y-8">
                <div>
                  <div className="field-label mb-3">Nature of Enquiry</div>
                  <div className="flex flex-wrap gap-2">
                    {TYPES.map((t) => (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => c("partner_type", t.id)}
                        data-testid={`partner-type-${t.id}`}
                        className={`px-5 py-2 text-xs tracking-[0.18em] uppercase border transition-all duration-500 ${
                          form.partner_type === t.id
                            ? "bg-[#C5A059] text-[#050814] border-[#C5A059]"
                            : "border-white/15 text-white/70 hover:border-[#C5A059]/60 hover:text-white"
                        }`}
                      >{t.label}</button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div><label className="field-label">Full Name *</label>
                    <input required data-testid="partner-name" className="field" value={form.name} onChange={(e)=>c("name",e.target.value)}/></div>
                  <div><label className="field-label">Email *</label>
                    <input required type="email" data-testid="partner-email" className="field" value={form.email} onChange={(e)=>c("email",e.target.value)}/></div>
                  <div><label className="field-label">Phone</label>
                    <input data-testid="partner-phone" className="field" value={form.phone} onChange={(e)=>c("phone",e.target.value)}/></div>
                  <div><label className="field-label">Country</label>
                    <input data-testid="partner-country" className="field" value={form.country} onChange={(e)=>c("country",e.target.value)}/></div>
                  <div><label className="field-label">Organisation *</label>
                    <input required data-testid="partner-org" className="field" value={form.organisation} onChange={(e)=>c("organisation",e.target.value)}/></div>
                  <div><label className="field-label">Your Role</label>
                    <input data-testid="partner-role" className="field" value={form.role} onChange={(e)=>c("role",e.target.value)}/></div>
                </div>

                <div>
                  <label className="field-label">A Few Words About Your Proposition</label>
                  <textarea data-testid="partner-message" className="field" value={form.message} onChange={(e)=>c("message",e.target.value)}/>
                </div>

                <button type="submit" data-testid="partner-submit" disabled={busy} className="btn-gold disabled:opacity-60">
                  {busy ? "Sending…" : (<>Begin a Conversation <ArrowRight className="ml-2" size={14}/></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
